import { Alert, Button, Col, Container, Row } from "react-bootstrap";
import QrCodeHelpButton from "./QrCodeHelpButton";
import { getRequestStatusMode } from "./requestStatus";
import { useRef, useState, useEffect, useMemo } from "react";
import { t } from "../locale";
import {
  captureSnapshot,
  useCamera,
  getRequestData,
  scrollToBottom,
  resizeImage,
  saveImage,
  stripDataUrlPrefix
} from "../helpers/helpers";
import 'bootstrap-icons/font/bootstrap-icons.css';
import { useNavigate } from "react-router-dom";
import QRCodeModal from "../components/QRCodeModal";
import KDocAnalyzeModal from "../components/KDocAnalyzeModal";
import StepGuide from "../components/StepGuide";
import { checkDocumentData, getQRCode, removeImageMetadata } from "../service/image";
import VerificationStepNav from "../components/VerificationStepNav";
import ExtractedDataModal from "../components/ExtractedDataModal";

const COUNTDOWN_SECONDS = 10;
const CAPTURE_DELAY_MS = 10000;
const RETRY_DELAY_MS = 5000;
const FAIL_DELAY_MS = 7000;
const BACK_RETRY_DELAY_MS = 5000;
const RESIZE_WIDTH = 1920;
const RESIZE_HEIGHT = 1440;

export interface DocumentSideState {
  buttonDisabled: boolean;
  borderColor: string;
  secondsLeft: number;
  showCountdown: boolean;
  isProcessing: boolean;
  statusMessage: string;
  blinkClass: string;
  isCaptured: boolean;
  imageSrc: string;
}

function DocumentVerification() {
  const navigate = useNavigate();

  const documentImageData = useRef('');
  const originalImageData = useRef('');
  const countDocCheckFront = useRef(0);
  const countDocCheckBack = useRef(0)

  const pathSegments = window.location.pathname.split("/");
  const companyId = pathSegments[1];
  const requestId = pathSegments[2];
  const passport = pathSegments[4];

  const isMobile = useMemo(() => {
    const ua = navigator.userAgent.toLowerCase()
    return /iphone|ipad|ipod|android|blackberry|windows phone/g.test(ua) ||
      /(ipad|macintosh|tablet|playbook|silk)|(android(?!.*mobile))/g.test(ua);
  }, [])
  const spaceHeight = isMobile ? '' : 'mt-5';

  const goBack = () => {
    navigate(`/${companyId}/${requestId}/p`)
  }

  const { mobileNav, desktopNav } = VerificationStepNav({
    currentStep: 2,
    companyId,
    requestId,
    onBack: goBack,
    spaceHeight
  })

  const [hasAccess, setHasAccess] = useState(true);
  const [expiredPage, setExpiredPage] = useState(false);

  const [facingMode, setFacingMode] = useState('environment');

  const mobileInstructionStyle = isMobile ? 'mt-4' : ''
  const frontButtonLabel = isMobile ? t('buttonFrontUploadMobile') : t('buttonFrontUpload')
  const backButtonLabel = isMobile ? t('buttonFrontUploadMobile') : t('backFrontUpload')

  const [isFrontStep, setIsFrontStep] = useState(true)
  const [statusColor, setStatusColor] = useState('text-primary')
  const [isBackVerified, setIsBackVerified] = useState(false)

  const [front, setFront] = useState<DocumentSideState>({
    buttonDisabled: false,
    borderColor: 'border-primary',
    secondsLeft: COUNTDOWN_SECONDS,
    showCountdown: true,
    isProcessing: false,
    statusMessage: t('failed'),
    blinkClass: '',
    isCaptured: false,
    imageSrc: '',
  })

  const [back, setBack] = useState<DocumentSideState>({
    buttonDisabled: true,
    borderColor: 'border-primary',
    secondsLeft: COUNTDOWN_SECONDS,
    showCountdown: false,
    isProcessing: false,
    statusMessage: '',
    blinkClass: '',
    isCaptured: false,
    imageSrc: '',
  })

  const updateFront = (updates: Partial<DocumentSideState>) =>
    setFront(prev => ({ ...prev, ...updates }));

  const updateBack = (updates: Partial<DocumentSideState>) =>
    setBack(prev => ({ ...prev, ...updates }));

  const [showModal, setShowModal] = useState(false);
  const [qrCodeSrc, setQrCodeSrc] = useState('');
  const [qrCodeMessage, setQrCodeMessage] = useState('');

  const [showAlert, setShowAlert] = useState(false);
  const [fields, setFields] = useState<string[] | undefined>([]);

  const [showExtractedModal, setShowExtractedModal] = useState(false);
  const [extractedData, setExtractedData] = useState<Record<string, string | null> | null>(null);
  const [fieldResults, setFieldResults] = useState<{ field: string, valid: boolean }[]>([]);

  const fileInputDocument = useRef<HTMLInputElement | null>(null);
  const fileInputBackDocument = useRef<HTMLInputElement | null>(null);
  const frontImageRef = useRef<HTMLDivElement | null>(null);
  const backImageRef = useRef<HTMLDivElement | null>(null);

  const scrollToImage = (ref: React.RefObject<HTMLDivElement | null>) => {
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
  };

  const videoElementIds = useMemo(() => [isFrontStep ? "videoElementFront" : "videoElementBack"], [isFrontStep]);
  useCamera(facingMode, isMobile, videoElementIds);

  useEffect(() => {
    fetchData();
    qrCode()
  }, [])

  const init = () => {
    countDocCheckFront.current = 0;
    countDocCheckBack.current = 0
    setFacingMode('environment')
    updateFront({
      isProcessing: false,
      showCountdown: true,
      secondsLeft: COUNTDOWN_SECONDS,
      borderColor: 'border-primary',
      isCaptured: false,
      buttonDisabled: false,
      imageSrc: '',
    })
    updateBack({
      secondsLeft: COUNTDOWN_SECONDS,
      isCaptured: false,
      imageSrc: '',
    })
  }

  const fetchData = async () => {
    try {
      const data = await getRequestData(companyId, requestId);

      if (data) {
        const statusMode = getRequestStatusMode(data?.status);
        if (statusMode) {
          navigate(`/${companyId}/${requestId}/status`, { replace: true });
          return;
        }

        if (data.status === "EXPIRED") {
          setExpiredPage(false);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setHasAccess(false);
    }
  }

  const isHeic = (file: File): boolean =>
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    file.name.toLowerCase().endsWith('.heic') ||
    file.name.toLowerCase().endsWith('.heif');

  const convertHeicToFile = async (file: File): Promise<File> => {
    const formData = new FormData();
    formData.append('file', file);
    const res = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/convert-image`, {
      method: 'POST',
      body: formData,
    });
    if (!res.ok) throw new Error('Konvertierung fehlgeschlagen');
    const { base64 } = await res.json() as { base64: string };
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], file.name.replace(/\.(heic|heif)$/i, '.png'), { type: 'image/png' });
  };

  const documentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {

    countDocCheckFront.current++;

    let file = event.target.files?.[0]

    if (file) {
      if (isHeic(file)) {
        try { file = await convertHeicToFile(file); } catch (e) { console.warn('HEIC conversion failed, using original', e); }
      }

      const reader = new FileReader()

      reader.onloadend = () => {
        const base64String = reader.result as string
        originalImageData.current = base64String.replace(/^data:image\/(jpg|jpeg|png|heic|heif|webp);base64,/, '')
      };
      reader.readAsDataURL(file);

      updateFront({ isCaptured: true, buttonDisabled: true })
      resizeImage(file, RESIZE_WIDTH, RESIZE_HEIGHT, async (resizedBase64) => {
        const base64String = resizedBase64.replace(/^data:image\/(jpg|jpeg|png|heic|heif|webp);base64,/, '')
        documentImageData.current = removeImageMetadata(base64String)
        saveBase64ToLocalStorage('documentImage', documentImageData.current)
        updateFront({
          showCountdown: false,
          imageSrc: "data:image/jpeg;base64," + resizedBase64,
          blinkClass: 'blinking-label',
          isProcessing: true,
          statusMessage: t('waitDocument'),
        })
        scrollToImage(frontImageRef)
        await compareImage()
      });
    }
    scrollToBottom()
  }

  const saveBase64ToLocalStorage = (key: string, base64: string) => {
    try {
      localStorage.setItem(key, base64);
    } catch (error) {
      console.error('Error saving Base64 string:', error);
    }
  };

  const handleBackSaveResult = (result: { status: boolean, message?: string }) => {
    const failureMessage = result.message || t('failed')

    if (result.status) {
      setQrCodeMessage('')
      updateBack({ borderColor: 'border-success', statusMessage: t('success'), isProcessing: false, blinkClass: '' })
      setIsBackVerified(true)
      scrollToImage(backImageRef)
    } else {
      updateBack({ borderColor: 'border-danger', statusMessage: failureMessage, isProcessing: false, blinkClass: '' })
      setIsBackVerified(false)
      setTimeout(() => {
        updateBack({ isCaptured: false, buttonDisabled: false, secondsLeft: COUNTDOWN_SECONDS })
      }, BACK_RETRY_DELAY_MS)
    }
  }

  const backDocumentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    countDocCheckBack.current++

    let file = event.target.files?.[0];
    if (file) {
      if (isHeic(file)) {
        try { file = await convertHeicToFile(file); } catch (e) { console.warn('HEIC conversion failed, using original', e); }
      }
      updateBack({ isCaptured: true })
      resizeImage(file, RESIZE_WIDTH, RESIZE_HEIGHT, async (resizedBase64) => {
        const base64String = stripDataUrlPrefix(resizedBase64)
        updateBack({
          statusMessage: t('waitDocument'),
          isProcessing: true,
          blinkClass: 'blinking-label',
          imageSrc: "data:image/jpeg;base64," + resizedBase64,
        })
        scrollToImage(backImageRef)
        const checkBackImage = await saveImage(base64String, companyId, requestId, false);
        handleBackSaveResult(checkBackImage);
      });
    }
    scrollToBottom()
  }

  const captureFrontId = async () => {
    countDocCheckFront.current++;

    updateFront({ buttonDisabled: true })
    const video = document.getElementById("videoElementFront") as HTMLVideoElement;

    if (!isMobile) {
      const countdown = setInterval(() => {
        setFront(prev => {
          if (prev.secondsLeft <= 1) {
            clearInterval(countdown);
            return { ...prev, secondsLeft: 0 };
          }
          return { ...prev, secondsLeft: prev.secondsLeft - 1 };
        });
      }, 1000);
    }

    updateFront({ isCaptured: true, buttonDisabled: true })
    setTimeout(async () => {
      if (!isMobile) {
        const result = await captureSnapshot(
          video,
          "DOCUMENT",
          documentImageData.current
        );

        updateFront({
          blinkClass: 'blinking-label',
          isProcessing: true,
          statusMessage: t('waitDocument'),
          showCountdown: false,
        })
        scrollToBottom();

        documentImageData.current = result.documentImageData;
        saveBase64ToLocalStorage('documentImage', documentImageData.current)
        updateFront({ imageSrc: "data:image/jpeg;base64," + documentImageData.current })
      }

      compareImage()
    }, CAPTURE_DELAY_MS);
  }

  const captureBackId = async () => {
    countDocCheckBack.current++
    const video = document.getElementById("videoElementBack") as HTMLVideoElement

    updateBack({ buttonDisabled: true })
    const countdown = setInterval(() => {
      setBack(prev => {
        if (prev.secondsLeft <= 1) {
          clearInterval(countdown);
          return { ...prev, secondsLeft: 0 };
        }
        return { ...prev, secondsLeft: prev.secondsLeft - 1 };
      });
    }, 1000);

    updateBack({ isCaptured: true })
    setTimeout(async () => {
      const result = await captureSnapshot(
        video,
        "DOCUMENT",
        documentImageData.current
      )

      updateBack({
        statusMessage: t('waitDocument'),
        isProcessing: true,
        blinkClass: 'blinking-label',
        imageSrc: "data:image/jpeg;base64," + result.documentImageData,
      })

      const checkBackImage = await saveImage(result.documentImageData, companyId, requestId, false)
      handleBackSaveResult(checkBackImage);

    }, CAPTURE_DELAY_MS);
  }

  const compareImage = async () => {
    const data = await checkDocumentData(documentImageData.current, requestId, companyId, 'id');

    if (data.validation === true) {
      changeCamera();
      setIsFrontStep(false)
      setStatusColor('text-success')
      updateFront({
        borderColor: 'border-success',
        isProcessing: true,
        isCaptured: true,
        statusMessage: t('success'),
        showCountdown: false,
        buttonDisabled: true,
        blinkClass: '',
      })
      updateBack({ showCountdown: true, buttonDisabled: false })
      scrollToImage(backImageRef)
    } else {
      if (countDocCheckFront.current === 1) {
        setStatusColor('text-danger')
        updateFront({
          borderColor: 'border-danger',
          blinkClass: '',
          statusMessage: t('failed'),
          secondsLeft: 0,
          buttonDisabled: true,
        })
      } else {
        setStatusColor('text-danger')
        updateFront({
          buttonDisabled: true,
          blinkClass: '',
          statusMessage: t('tryAgain'),
          borderColor: 'border-danger',
        })
      }
      if (!isMobile) {
        setQrCodeMessage('changeDeviceText')
        setShowModal(true)
      }
    }

    if (data.extracted) {
      setExtractedData(data.extracted)
      setFieldResults(data.data ?? [])
      setShowExtractedModal(true)
    } else if (data.validation !== true) {
      const delay = countDocCheckFront.current === 1 ? FAIL_DELAY_MS : RETRY_DELAY_MS
      setTimeout(() => init(), delay)
    }
  }

  const changeCamera = () => {
    setFacingMode((prevMode) => (prevMode === "user" ? "environment" : "user"));
  }

  const handleDocumentUpload = () => {
    if (fileInputDocument.current) {
      fileInputDocument.current.click();
    }
  }

  const handleBackDocumentUpload = () => {
    if (fileInputBackDocument.current) {
      fileInputBackDocument.current.click();
    }
  }

  const qrCode = async () => {
    const res = await getQRCode(companyId, requestId, passport)
    setQrCodeSrc(res.qrCode)
  }

  const next = () => {
    navigate(`/${companyId}/${requestId}/face_identify`)
  }

  const handleClose = () => {
    setShowModal(false);
    if (front.borderColor === 'border-danger') {
      init();
    }
  };

  const handleCloseFieldPrompt = () => {
    setShowAlert(false)
  }
  const bothModalsVisible = showModal && showExtractedModal;

  return (
    <>
      {!isMobile && (
        <QRCodeModal
          qrCode={qrCodeSrc}
          message={qrCodeMessage}
          show={showModal}
          onClose={handleClose}
          position={bothModalsVisible ? 'left' : 'center'}
        />
      )}
      <KDocAnalyzeModal show={showAlert} fields={fields} onClose={handleCloseFieldPrompt} />

      {mobileNav}

      {hasAccess ? (
        <Container hidden={expiredPage} className={`overflow-hidden ${spaceHeight}`}>
          <Row className="row overflow-auto">
            {desktopNav}

            <Col className={`d-flex flex-column h-sm-100 px-sm-5 ${spaceHeight}`}>
              <div className="d-flex justify-content-end gap-2">
                {!isMobile && <QrCodeHelpButton
                  onClick={() => {
                    setQrCodeMessage('');
                    setShowModal(true);
                  }}
                />}
                <button className="btn btn-sm btn-outline-secondary rounded-pill"
                  type="button" data-bs-toggle="offcanvas" data-bs-target="#offcanvasRight"
                  aria-controls="offcanvasRight">{t('instructions')}</button>
              </div>
              <main className={`row overflow-auto px-sm-5 position-relative ${spaceHeight}`}>
                <Col className="pt-4 pt-sm-5 text-center text-sm-start mt-n1">
                  <h4 className="mb-4">{t('titleVideoIdentify')}</h4>
                  <Row className="mt-5 mt-sm-0">
                    <div className={`col-sm-5 ${mobileInstructionStyle}`}>
                      <h5 className="mb-2">{t('titleFrontId')}</h5>

                      {!isMobile && (
                        <div
                          id="videoContainer"
                          className={` border ${front.borderColor} border-2 rounded-3 id-frame`}
                        >
                          {isFrontStep && (
                            <video
                              className="w-100 h-100 rounded-2 object-fit-cover"
                              id="videoElementFront"
                              autoPlay
                              muted
                              style={{ transform: 'scaleX(-1)' }}
                            />
                          )}

                          {front.showCountdown && front.secondsLeft !== 0 && (
                            <span className="text-primary h5">
                              {front.secondsLeft}
                            </span>
                          )}
                        </div>
                      )}

                      <div className="text-center mt-3">
                        <div className="mt-5">
                          <StepGuide
                            step={1}
                            text={t('guideStep1Front')}
                            show={!front.isCaptured && !front.buttonDisabled}
                            arrowAlign={isMobile ? 'center' : 'left'}
                          />
                        </div>
                        <Button
                          id="startProcessButton"
                          className={`btn btn-outline-primary rounded-pill d-none d-sm-inline ${front.buttonDisabled ? 'disabled' : ''}`}
                          onClick={captureFrontId}
                          style={{ textTransform: 'none' }}
                        >
                          {t('buttonFrontCapture')}
                        </Button>

                        <p className="my-2 d-none d-sm-block text-muted small">{t('or')}</p>

                        <input
                          type="file"
                          accept="image/*,.heic,.heif;capture=camera"
                          onChange={documentUpload}
                          ref={fileInputDocument}
                          style={{ display: 'none' }}
                        />

                        <Button
                          className={`btn btn-outline-primary rounded-pill ${front.buttonDisabled ? 'disabled' : ''}`}
                          onClick={handleDocumentUpload}
                          style={{ textTransform: 'none' }}
                        >
                          {frontButtonLabel}
                        </Button>
                      </div>
                      <div className="mt-4 mb-4">
                        {isMobile && front.isProcessing && <span className={`text-primary h4 ${front.blinkClass} ${statusColor}`}>{front.statusMessage}</span>}
                      </div>


                      {front.isCaptured && <div ref={frontImageRef} className={`text-center mt-2 rounded-3 border border-3 id-frame w-auto ${front.borderColor}`}>
                        <img
                          src={front.imageSrc}
                          className={`w-100 h-100 object-fit-cover`}
                          style={{ height: '200px' }}
                          alt=""
                        />
                        <div className="mt-2">
                          {!isMobile && front.isProcessing && <span className={`text-primary h5 ${front.blinkClass} ${statusColor}`}>{front.statusMessage}</span>}
                        </div>

                      </div>
                      }

                    </div>

                    {/* BACK */}
                    <div className={`col-sm-5 ${mobileInstructionStyle}`}>
                      <h5 className="mb-2">{t('titleBackId')}</h5>

                      {!isMobile && (
                        <div className={`border ${back.borderColor} border-2 rounded-3 id-frame`}>
                          <video
                            className="w-100 h-100 rounded-2 object-fit-cover"
                            id="videoElementBack"
                            autoPlay
                            muted
                            style={{ transform: 'scaleX(-1)' }}
                          />
                          {back.showCountdown && (back.secondsLeft !== 0 && <span className="text-primary h5">{back.secondsLeft}</span>)}
                        </div>
                      )}

                      <div className="text-center mt-3">
                        <div className="mt-5">
                          <StepGuide
                            step={2}
                            text={t('guideStep2Back')}
                            show={front.borderColor === 'border-success' && !back.isCaptured && !back.buttonDisabled}
                            arrowAlign={isMobile ? 'center' : 'left'}
                          />
                        </div>
                        <Button
                          className={`btn btn-outline-primary rounded-pill d-none d-sm-inline ${back.buttonDisabled ? 'disabled' : ''}`}
                          onClick={captureBackId}
                          style={{ textTransform: 'none' }}
                        >
                          {t('backFrontCapture')}
                        </Button>

                        <p className="my-2 d-none d-sm-block text-muted small">{t('or')}</p>

                        <input
                          type="file"
                          accept="image/*,.heic,.heif;capture=camera"
                          onChange={backDocumentUpload}
                          ref={fileInputBackDocument}
                          style={{ display: 'none' }}
                        />

                        <Button
                          className={`btn btn-outline-primary rounded-pill ${back.buttonDisabled ? 'disabled' : ''}`}
                          onClick={handleBackDocumentUpload}
                          style={{ textTransform: 'none' }}
                          aria-busy={back.isProcessing || undefined}
                        >
                          {backButtonLabel}
                          {back.isProcessing && <span className="ms-2" role="status" aria-hidden="true" />}
                        </Button>
                      </div>
                      {back.isCaptured &&
                        <div ref={backImageRef} className={`text-center mt-4 rounded-3 border border-3 id-frame w-auto ${back.borderColor}`}>
                          <img
                            src={back.imageSrc}
                            className="w-100 h-100 object-fit-cover"
                            style={{ height: '200px' }}
                            alt=""
                          />
                          <div className="mt-2">
                            {!isMobile && !!back.statusMessage && <span className={`h5 ${back.blinkClass} ${back.borderColor === 'border-danger' ? 'text-danger' : back.borderColor === 'border-success' ? 'text-success' : 'text-primary'}`}>{t(back.statusMessage)}</span>}
                          </div>
                          <div className="mt-4 mb-4">
                            {isMobile && !!back.statusMessage && <span className={`h4 ${back.blinkClass} ${back.borderColor === 'border-danger' ? 'text-danger' : back.borderColor === 'border-success' ? 'text-success' : 'text-primary'}`}>{t(back.statusMessage)}</span>}
                          </div>
                        </div>
                      }
                    </div>
                  </Row>

                  {isBackVerified && (
                    <div className="d-flex flex-column align-items-end">
                      <div style={{ maxWidth: '280px' }}>
                        <StepGuide step={3} text={t('guideStep3Next')} show={true} arrowAlign="right" />
                      </div>
                      <button
                        className={`btn btn-success ${isMobile ? 'mt-2' : 'mt-1'} px-4`}
                        type="submit"
                        onClick={next}
                      >
                        {t('nextStep')} »
                      </button>
                    </div>
                  )}

                  <p className="my-5">&nbsp;</p>
                </Col>

                <div className="offcanvas offcanvas-end" id="offcanvasRight" aria-labelledby="offcanvasRightLabel" data-bs-scroll="true">
                  <div className="offcanvas-header">
                    <h5 id="offcanvasRightLabel" className="mb-0">{t('instructions')}</h5>
                    <button type="button" className="btn-close" data-bs-dismiss="offcanvas" aria-label="Close" />
                  </div>
                  <div className="offcanvas-body">
                    <p className="mb-3 text-muted">{t('bestResults')}</p>
                    <ul className="text-start list-unstyled mb-4 small">
                      <li>• {t('firstInstruction')}</li>
                      <li>• {t('secondInstruction')}</li>
                      <li>• {t('thirdInstruction')}</li>
                    </ul>

                    <p className="mb-2 text-muted">{t('example')}</p>

                    <p className="mb-1">{t('frontSide')}</p>
                    <img src="/images/Front id.jpg" className="img-fluid rounded-2 border minimal-surface mb-3" alt="" />

                    <p className="mb-1">{t('backSide')}</p>
                    <img src="/images/Back is.jpg" className="img-fluid rounded-2 border minimal-surface" alt="" />
                  </div>
                </div>
              </main>
            </Col>
          </Row>
        </Container>
      ) : (
        <Alert variant="danger" className="mt-3">
          {t("accessDenied")}
        </Alert>
      )}

      <ExtractedDataModal
        show={showExtractedModal}
        data={extractedData}
        fieldResults={fieldResults}
        position={bothModalsVisible ? 'right' : 'center'}
        backdrop={!bothModalsVisible}
        onConfirm={() => {
          setShowExtractedModal(false)
          if (front.borderColor !== 'border-success') {
            const invalidFields = fieldResults.filter(r => !r.valid).map(r => r.field)
            if (invalidFields.length > 0) {
              navigate(`/${companyId}/${requestId}/p`, { state: { invalidFields } })
              return
            }
            init()
          }
        }}
      />

      <div className="py-2" hidden={!expiredPage}>
        <h4>{t("requestExpired")}</h4>
        <p>{t("accessExpired")}</p>
      </div>
    </>
  );
};

export default DocumentVerification;